import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  loginUser,
  verifyLoginOtp,
  registerUser,
  verifyRegisterOtp,
  logoutUser,
  getCurrentUser,
  initializeAuth,
  clearError,
  clearPendingOtp,
  clearPendingRegisterOtp,
  setRequirePasswordChange,
  setRequireEmailVerification,
  selectAuth,
  selectUser,
  selectIsAuthenticated,
  selectIsLoading,
  selectError,
  selectRequirePasswordChange,
  selectRequireEmailVerification,
  selectPendingOtp,
  selectPendingRegisterOtp,
} from '@/store/slices/authSlice';
import { ROUTES } from '@/constants';
import type { LoginCredentials, RegisterCredentials } from '@/types';

/**
 * Custom hook for authentication operations
 */
export const useAuth = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Selectors
  const auth = useAppSelector(selectAuth);
  const user = useAppSelector(selectUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectIsLoading);
  const error = useAppSelector(selectError);
  const requirePasswordChange = useAppSelector(selectRequirePasswordChange);
  const requireEmailVerification = useAppSelector(selectRequireEmailVerification);
  const pendingOtp = useAppSelector(selectPendingOtp);
  const pendingRegisterOtp = useAppSelector(selectPendingRegisterOtp);

  // Initialize authentication state by checking HTTP-only cookie with backend
  const initialize = useCallback(async () => {
    await dispatch(initializeAuth());
  }, [dispatch]);

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      const result = await dispatch(loginUser(credentials));
      if (loginUser.fulfilled.match(result)) {
        const payload = result.payload as { user?: { role: string }; requiresOtp?: boolean };
        if (payload.requiresOtp) {
          // Stay on login page; OTP step will be shown
          return result;
        }
        if (payload.user?.role) {
          if (payload.user.role === 'admin') {
            navigate(ROUTES.ADMIN_DASHBOARD);
          } else if (payload.user.role === 'vendor') {
            navigate(ROUTES.VENDOR_DASHBOARD);
          } else {
            navigate(ROUTES.DASHBOARD);
          }
        }
      }
      return result;
    },
    [dispatch, navigate]
  );

  const submitLoginOtp = useCallback(
    async (otp: string) => {
      if (!pendingOtp?.tempToken) return null;
      const result = await dispatch(verifyLoginOtp({ tempToken: pendingOtp.tempToken, otp }));
      if (verifyLoginOtp.fulfilled.match(result)) {
        const userRole = result.payload.user.role;
        if (userRole === 'admin') {
          navigate(ROUTES.ADMIN_DASHBOARD);
        } else if (userRole === 'vendor') {
          navigate(ROUTES.VENDOR_DASHBOARD);
        } else {
          navigate(ROUTES.DASHBOARD);
        }
      }
      return result;
    },
    [dispatch, navigate, pendingOtp?.tempToken]
  );

  const register = useCallback(
    async (credentials: RegisterCredentials) => {
      const result = await dispatch(registerUser(credentials));
      if (registerUser.fulfilled.match(result)) {
        const payload = result.payload as { user?: { role: string }; requiresOtp?: boolean };
        if (payload.requiresOtp) return result;
        if (payload.user?.role) {
          if (payload.user.role === 'admin') navigate(ROUTES.ADMIN_DASHBOARD);
          else if (payload.user.role === 'vendor') navigate(ROUTES.VENDOR_DASHBOARD);
          else navigate(ROUTES.DASHBOARD);
        }
      }
      return result;
    },
    [dispatch, navigate]
  );

  const submitRegisterOtp = useCallback(
    async (otp: string) => {
      if (!pendingRegisterOtp?.tempToken) return null;
      const result = await dispatch(
        verifyRegisterOtp({ tempToken: pendingRegisterOtp.tempToken, otp })
      );
      if (verifyRegisterOtp.fulfilled.match(result)) {
        const auth = result.payload as { user?: { role?: string } };
        const userRole = auth?.user?.role;
        if (userRole === 'admin') navigate(ROUTES.ADMIN_DASHBOARD, { replace: true });
        else if (userRole === 'vendor') navigate(ROUTES.VENDOR_DASHBOARD, { replace: true });
        else navigate(ROUTES.DASHBOARD, { replace: true });
      }
      return result;
    },
    [dispatch, navigate, pendingRegisterOtp?.tempToken]
  );

  const logout = useCallback(async () => {
    await dispatch(logoutUser());
    navigate(ROUTES.LOGIN);
  }, [dispatch, navigate]);

  const getProfile = useCallback(async () => {
    return await dispatch(getCurrentUser());
  }, [dispatch]);

  const clearAuthError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const setPasswordChangeRequired = useCallback(
    (required: boolean) => {
      dispatch(setRequirePasswordChange(required));
    },
    [dispatch]
  );

  const setEmailVerificationRequired = useCallback(
    (required: boolean) => {
      dispatch(setRequireEmailVerification(required));
    },
    [dispatch]
  );

  // Check if user has specific role
  const hasRole = useCallback(
    (role: string) => {
      return user?.role === role;
    },
    [user]
  );

  // Check if user can access admin features
  const isAdmin = useCallback(() => {
    return user?.role === 'admin';
  }, [user]);

  // Check if user is regular user
  const isUser = useCallback(() => {
    return user?.role === 'user';
  }, [user]);

  // Check if user is vendor
  const isVendor = useCallback(() => {
    return user?.role === 'vendor';
  }, [user]);

  // Check if vendor is approved
  const isVendorApproved = useCallback(() => {
    if (!user || user.role !== 'vendor') {
      return false;
    }
    return user.vendorApprovalStatus === 'approved';
  }, [user]);

  // Get user's full name
  const getUserFullName = useCallback(() => {
    if (!user) return '';
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  }, [user]);

  // Get user's initials
  const getUserInitials = useCallback(() => {
    if (!user) return '';
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
  }, [user]);

  return {
    // State
    auth,
    user,
    isAuthenticated,
    isLoading,
    error,
    requirePasswordChange,
    requireEmailVerification,
    pendingOtp,
    pendingRegisterOtp,

    // Actions
    initialize,
    login,
    submitLoginOtp,
    register,
    submitRegisterOtp,
    logout,
    getProfile,
    clearError: clearAuthError,
    clearPendingOtp: () => dispatch(clearPendingOtp()),
    clearPendingRegisterOtp: () => dispatch(clearPendingRegisterOtp()),
    setPasswordChangeRequired,
    setEmailVerificationRequired,

    // Utility functions
    hasRole,
    isAdmin,
    isUser,
    isVendor,
    isVendorApproved,
    getUserFullName,
    getUserInitials,
  };
};